import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";

export interface gifFrame {
  id: string;
  type: string;
  text?: string;
  image?: string;
  fontSize?: number;
}

export interface Asset {
  id: string;
  imageUrl: string;
}

export interface ButtonGifResponse {
  frames: gifFrame[];
  backgroundColors: string[];
  logos: Asset[];
}

export const useButtonGifStore = defineStore("buttonGif", {
  state: (): {
    frames: gifFrame[];
    backgroundColors: string[];
    selectedBgColor: string;
    selectedFrameId: string;
    assets: Asset[];
    isLoading: boolean;
    creativeTemplateId: number | null;
    media: Blob;
    generationFailed: boolean;
    uniqueId: string;
    triggerUpdateCanvas: boolean;
  } => ({
    frames: [],
    backgroundColors: [],
    selectedBgColor: "",
    selectedFrameId: "",
    assets: [],
    isLoading: false,
    creativeTemplateId: null,
    media: "" as unknown as Blob,
    generationFailed: false,
    uniqueId: "",
    triggerUpdateCanvas: false,
  }),
  actions: {
    setFrames(frames: gifFrame[]) {
      this.frames = frames;
    },
    setBackgroundColors(colors: string[]) {
      this.backgroundColors = colors;
    },
    setSelectedBgColor(color: string) {
      // If the color is already in the list, just select it
      if (this.backgroundColors.includes(color)) {
        this.selectedBgColor = color;
        return;
      }
      // Otherwise, add the color to the list and select it
      this.backgroundColors.push(color);
      this.selectedBgColor = color;
    },
    setSelectedFrameId(id: string) {
      this.selectedFrameId = id;
    },
    setSelectedAssetForTheFrame(imageUrl: string) {
      if (!imageUrl) return;
      const frameIdx = this.frames.findIndex(
        (fr) => fr.id === this.selectedFrameId
      );
      if (frameIdx !== -1 && this.frames[frameIdx].type === "image") {
        this.frames[frameIdx].image = imageUrl;
      }
    },
    setFrameType(type: string) {
      const frameIdx = this.frames.findIndex(
        (fr) => fr.id === this.selectedFrameId
      );
      if (frameIdx !== -1) {
        this.frames[frameIdx].type = type;
      }
    },
    setIsLoading(value: boolean) {
      this.isLoading = value;
    },
    setState(buttonGifResponse: ButtonGifResponse) {
      this.frames =
        buttonGifResponse.frames.length > 5
          ? buttonGifResponse.frames.slice(0, 5)
          : buttonGifResponse.frames;
      this.backgroundColors = buttonGifResponse.backgroundColors;
      this.selectedBgColor = buttonGifResponse.backgroundColors[0];
      this.selectedFrameId = buttonGifResponse.frames[0]?.id || "";
      this.assets = buttonGifResponse.logos;
    },
    addNewAsset(asset: Asset) {
      this.assets.unshift(asset);
    },
    setAssets(assets: Asset[]) {
      this.assets = assets;
    },
    setTriggerUpdateCanvas(value: boolean) {
      this.triggerUpdateCanvas = value;
    },
    reset() {
      this.frames = [];
      this.backgroundColors = [];
      this.selectedBgColor = "";
      this.selectedFrameId = "";
      this.assets = [];
      this.isLoading = false;
      this.creativeTemplateId = null;
      this.media = "" as unknown as Blob;
      this.generationFailed = false;
      this.uniqueId = "";
      this.triggerUpdateCanvas = false;
    },
    swapFrames(fromIndex: number, toIndex: number) {
      const temp = this.frames[fromIndex];
      this.frames[fromIndex] = this.frames[toIndex];
      this.frames[toIndex] = temp;
    },
    addNewFrame(frame: gifFrame) {
      this.frames.push(frame);
    },
    deleteFrame(frameId: string) {
      const frameIdx = this.frames.findIndex((fr) => fr.id === frameId);
      if (frameIdx !== -1) {
        this.frames.splice(frameIdx, 1);
      }
    },
    updateFrame(frame: any) {
      const frameIdx = this.frames.findIndex((fr) => fr.id === frame.id);
      if (frameIdx !== -1) {
        const updatedFrame = { ...this.frames[frameIdx], ...frame };
        this.frames[frameIdx] = updatedFrame;
      }
    },
    setCreativeTemplateId(id: number | null) {
      this.creativeTemplateId = id;
    },
    setMedia(media: Blob) {
      this.media = media;
    },
    setGenerationFailed(failed: boolean) {
      this.generationFailed = failed;
    },
    setUniqueId(id?: string) {
      if (id) {
        this.uniqueId = id;
        return;
      }
      this.uniqueId = uuidv4();
    },
  },
});
